import { useEffect, useRef } from "react";
import { Shield, Zap, Target, Lock, Code2, Layers, Award, TrendingUp, Cpu, FileText, Bot, Search } from "lucide-react";

const FEATURES = [
  { icon: Zap, title: "Fast API Responses", desc: "Sub-second OCR processing. Handles thousands of documents per minute with consistent latency." },
  { icon: Shield, title: "Enterprise Secure", desc: "Data encrypted in transit and at rest. Your documents and CVs are never stored after processing." },
  { icon: Bot, title: "ATS CV Scoring", desc: "Instant ATS score out of 100. Know exactly how recruiters' systems rank your CV before you apply." },
  { icon: Search, title: "Job Description Matching", desc: "Paste any JD and get a keyword match rate, missing skills, and gap analysis in seconds." },
  { icon: Code2, title: "Developer First", desc: "Clean REST APIs, full documentation, and a sandbox for rapid integration into your workflow." },
  { icon: TrendingUp, title: "Data-Driven Insights", desc: "Track OCR accuracy, CV score history, and extraction performance across all your documents." },
  { icon: FileText, title: "Multi-Format Support", desc: "PDF, JPG, PNG up to 10 pages. Structured JSON output with confidence scores per field." },
  { icon: Award, title: "99.9% Uptime SLA", desc: "Mission-critical reliability with redundant infrastructure. Always on when you need it." },
  { icon: Cpu, title: "AI-Powered OCR", desc: "Powered by Cloudflare Workers AI — accurate, fast, and privacy-first document processing." },
  { icon: Lock, title: "Privacy-First", desc: "No document storage. CVs and invoices are processed in memory and discarded immediately." },
  { icon: Layers, title: "Free Scans & Credits", desc: "Start with 3 free ATS scans and 10 OCR credits on sign-up. Credits never expire." },
  { icon: Target, title: "Keyword Gap Analysis", desc: "See exactly which ATS keywords are missing from your CV vs the target job description." },
];

export default function Features() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          entries[0].target.querySelectorAll(".feature-item").forEach((el, i) => {
            setTimeout(() => el.classList.add("visible"), i * 55);
          });
        }
      },
      { threshold: 0.08 }
    );
    const el = sectionRef.current;
    if (el) observer.observe(el);
    return () => el && observer.unobserve(el);
  }, []);

  return (
    <section id="features" className="py-24 md:py-32 px-6 md:px-10 section-light" data-testid="features-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <span className="overline-tag block mb-3">Why Tyo Solution</span>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4">Built for speed, reliability, and scale.</h2>
          <p className="text-[#94A3B8] text-lg max-w-xl mx-auto leading-relaxed">
            Every feature designed for job seekers, developers, and production workloads.
          </p>
        </div>

        <div ref={sectionRef} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="features-grid">
          {FEATURES.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.title}
                className="feature-item glass-card p-5 group"
                data-testid={`feature-item-${i}`}
              >
                <div className="w-9 h-9 rounded-xl bg-[#38BDF8]/10 flex items-center justify-center mb-4 group-hover:bg-[#38BDF8]/20 transition-colors">
                  <Icon size={17} className="text-[#38BDF8]" />
                </div>
                <h3 className="text-sm font-semibold text-white mb-1.5">{feature.title}</h3>
                <p className="text-xs text-[#64748B] leading-relaxed">{feature.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
